// Work-order domain types for the frontend.
//
// The canonical contract (enums, Selection) lives in `@atlas/types`. This module
// re-exports the enums and defines the FRONTEND VIEW shape, aligned with the
// work-order-service DTO (orderCode, status, tuple, assigneeIds, …). The MSW
// mock seed produces this same shape, so mock and live API are interchangeable.

import type { WorkOrderPackUpdateInput } from '@atlas/models'
import type {
	DTOOf,
	IDeliverableReview,
	IMoney,
	IOriginalCode,
	IReferencedDocument,
	IWorkOrderChild,
	IWorkOrderExternalFields,
	IWorkOrderPack,
	IWorkOrderPrice,
	Selection,
} from '@atlas/types'
import {
	AdaptationSource,
	AssetTypeCode,
	ComplexityLevel,
	DEFAULT_CURRENCY,
	isWithdrawal,
	MAX_ORIGINAL_CODE_LENGTH,
	MAX_ORIGINAL_CODES,
	OriginalCodeSource,
	ReviewVerdict,
	WorkflowPhase,
	WorkOrderKind,
	WorkOrderLinkSource,
	WorkOrderSource,
} from '@atlas/types'
import { DONE_VISIBLE_DAYS } from '@/config/board'
import type { FileWithTagsDTO } from '@/services/file.service'
import type { ResolvedContext } from './resolved-context.types'
import type { ResolvedWorkflowSnapshot } from './workflow.types'

export type { Selection }
export {
	AdaptationSource,
	AssetTypeCode,
	ComplexityLevel,
	DEFAULT_CURRENCY,
	MAX_ORIGINAL_CODE_LENGTH,
	MAX_ORIGINAL_CODES,
	OriginalCodeSource,
	ReviewVerdict,
	WorkflowPhase,
	WorkOrderKind,
	WorkOrderLinkSource,
	WorkOrderSource,
}

/** Money as the API carries it: integer minor units and an ISO 4217 code. */
export type Money = IMoney
/** One original Veeva code of a work order, at its place in the list. */
export type OriginalCode = IOriginalCode
/** A Vault document a work order's brief references. */
export type ReferencedDocument = IReferencedDocument
/** One row of a parent's Children table. */
export type WorkOrderChild = DTOOf<IWorkOrderChild>
/** A child's price, as the Workfront sync took it from the price list. */
export type WorkOrderPrice = DTOOf<IWorkOrderPrice>
/** What a work order carries about the external system it came from, plus its money and delivery facts. */
export type WorkOrderExternalFields = DTOOf<IWorkOrderExternalFields>

/**
 * One column the board renders: a status of a workflow, as the work-order read
 * model returns it. Codes are workflow configuration, so neither the code nor
 * the label may be derived from an enum here. `isTerminal` and `phase` are the
 * status's own configuration — finished-state and review-stage behavior read
 * these instead of matching a status code.
 */
export interface BoardStatus {
	code: string
	label: string
	position: number
	isTerminal: boolean
	phase: WorkflowPhase
}

/** A workflow the board groups by, with the columns it renders in configured order. */
export interface BoardWorkflow {
	workflowId: string
	name: string
	statuses: BoardStatus[]
}

/**
 * A display label for a status code outside the board, where the workflow's
 * configured labels are not at hand: the code humanized (`internal_validation`
 * → "Internal Validation"). The board proper uses `BoardStatus.label`, which is
 * the administrator's own text and always wins over this.
 */
export function statusLabel(code: string): string {
	return code
		.split('_')
		.filter(Boolean)
		.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
		.join(' ')
}

/**
 * A work order as rendered by the board/detail — mirrors the work-order-service
 * `GET /work-orders/{id}` DTO. `workOrderId` (uuid) is internal; `orderCode`
 * (`WO-NNNNNN`) is the human reference. Descriptive values come from the
 * classification tuple/attributes, hydrated onto the read shape.
 *
 * The external fields are null on a manual order. `externalOwnerEmail` is only
 * on the single-order read; the board's list omits it.
 */
/** The order's pack as the API returns it: dates as ISO strings. */
export type WorkOrderPack = Omit<IWorkOrderPack, 'updatedAt'> & { updatedAt: string | null }

export interface WorkOrder extends Omit<WorkOrderExternalFields, 'externalOwnerEmail'> {
	externalOwnerEmail?: string | null
	workOrderId: string
	orderCode: string
	title: string
	briefing: string | null
	source: WorkOrderSource
	externalRef: string | null
	/** A status code of the assigned workflow — configuration, not a closed enum. */
	status: string
	/** The workflow the platform resolved; null means the order is unconfigured. */
	workflowId: string | null
	/** The project manager accountable for the order (a user id), or null. */
	pmUserId: string | null
	/** False marks a petition handled outside the Content Factory. */
	isContentFactory: boolean
	dueAt: string | null // ISO datetime (date + time, SLA)
	parentWorkOrderId: string | null
	kind: WorkOrderKind
	statusChangedAt: string // ISO datetime — when status last changed
	tuple: Selection
	complexityLevel: ComplexityLevel | null
	assetTypeCode: AssetTypeCode | null
	/** The TARGET Veeva code — the code this asset is produced FOR. */
	veevaCode: string | null
	durationSeconds: number | null
	/** The linked Campaign's immutable `campaignCode`. */
	campaignCode: string | null
	assigneeIds: string[]
	links: RelatedLink[]
	createdAt: string
	/** How many live children the order has; on the board's list only. */
	childrenCount?: number
	/** The original Veeva codes, in order; on the single-order read only. */
	originalCodes?: OriginalCode[]
	/** The Vault documents the brief references; on the single-order read only. */
	referencedDocuments?: ReferencedDocument[]
	/** The `aspect_ratio` value code; on the single-order read only. */
	aspectRatio?: string | null
	/** What the PM wrote down for the designer; on the single-order read only. */
	pack?: WorkOrderPack
	/**
	 * Whether the caller may refresh the order from Workfront: a Workfront order, the refresh
	 * enabled, and the caller its PM or an admin. On the detail aggregate
	 * (`GET /work-orders/{id}/detail`) only.
	 */
	refreshFromWorkfrontAllowed?: boolean
	/** A child's price from the price list; null when it was never priced. On the detail aggregate only. */
	price?: WorkOrderPrice | null
}

/** A manual work order's external fields: all empty. For mocks, fixtures and stories. */
export const NO_EXTERNAL_FIELDS: Omit<WorkOrderExternalFields, 'externalOwnerEmail'> = {
	externalObjectType: null,
	externalReferenceNumber: null,
	externalStatus: null,
	externalStatusLabel: null,
	externalClosedAt: null,
	externalRemovedAt: null,
	externalUpdatedAt: null,
	externalVersion: null,
	externalSyncedAt: null,
	externalOwnerName: null,
	externalCostStatus: null,
	plannedAssetCount: null,
	budget: null,
	plannedCost: null,
	finalCost: null,
	expectedPublishingDate: null,
	isAdaptation: null,
	isAdaptationSource: null,
	adaptationNeeds: null,
	scopeChanged: null,
	scopeChangeReason: null,
	externalDescription: null,
	externalHold: null,
}

/** An original code as a person enters it; the server keeps the order and finds the link. */
export interface OriginalCodeInput {
	code: string
	description?: string | null
	link?: string | null
}

/** An amount as a person enters it; the currency defaults to USD on the server. */
export interface MoneyInput {
	amountMinor: number
	currency?: string
}

/**
 * A deliverable as the detail aggregate returns it: the discovery shape plus a
 * thumbnail URL the server has already presigned.
 *
 * `thumbnailUrl` is absent — not null — while a file's `thumbnail_generation`
 * concern is unfinished or does not apply, which is the normal state right
 * after upload.
 */
export type DeliverableFile = FileWithTagsDTO & { thumbnailUrl?: string }

/** A reviewer's mark on one final visual, on the review stage the order sits on. */
export type DeliverableReview = DTOOf<IDeliverableReview>

/**
 * Everything the detail page renders, from one request.
 *
 * `deliverables` is keyed by `deliverable_type`; each box selects its own bucket
 * from this single cached object instead of issuing its own discover call, so a
 * deliverable mutation invalidates one key and refetches one request.
 */
export interface WorkOrderDetail extends WorkOrder {
	context: ResolvedContext
	deliverables: Record<string, DeliverableFile[]>
	children: WorkOrder[]
	parent: WorkOrder | null
	/** The final visuals marked reviewed on the current review stage; empty off one. */
	reviews: DeliverableReview[]
	/** Legal transitions + guards + lane for the state timeline/action bar (design.md D7). `null` under the same conditions as `WorkOrderDetailRead.workflow`. */
	workflowSnapshot: ResolvedWorkflowSnapshot | null
}

/**
 * {@link WorkOrderDetail} as it arrives, before `context` is normalized —
 * also lacks `workflowSnapshot`, which the wire response carries as a
 * sibling of `data`, not inside it (see `workOrderApi.detail`).
 */
export interface RawWorkOrderDetail extends Omit<WorkOrderDetail, 'context' | 'workflowSnapshot'> {
	context: unknown
}

/** A named related link attached to a work order (Veeva vault, Workfront, brief…). */
export interface WorkOrderLink {
	name: string
	url: string
}

/**
 * A related link as the work order holds it: a manual one a person keeps, or an external one
 * the sync mirrors from the external system, which the edit form leaves alone.
 */
export interface RelatedLink extends WorkOrderLink {
	source: WorkOrderLinkSource
}

/**
 * Merges a patch's manual links into an order's related links.
 *
 * External links (mirrored from an external system) are preserved unchanged.
 * The patch's links replace any previous manual links and are given source MANUAL.
 * When `patchLinks` is undefined, the current links are left untouched.
 */
export function patchWorkOrderLinks(current: RelatedLink[] = [], patchLinks?: WorkOrderLink[]): RelatedLink[] {
	if (patchLinks === undefined) return current
	const external = current.filter((link) => link.source === WorkOrderLinkSource.EXTERNAL)
	const manual = patchLinks.map((link) => ({ ...link, source: WorkOrderLinkSource.MANUAL }))
	return [...external, ...manual]
}

/** Body for manually creating a work order — mirrors `workOrderCreateSchema`. */
export interface CreateWorkOrderInput {
	title: string
	briefing?: string
	source?: WorkOrderSource
	dueAt?: string // ISO datetime
	parentWorkOrderId?: string
	pmUserId?: string
	isContentFactory?: boolean
	tuple?: Selection
	complexityLevel?: ComplexityLevel
	assetTypeCode?: AssetTypeCode
	veevaCode?: string
	durationSeconds?: number
	campaignCode?: string
	links?: WorkOrderLink[]
	assigneeIds?: string[]
	budget?: MoneyInput
	plannedCost?: MoneyInput
	finalCost?: MoneyInput
	expectedPublishingDate?: string // YYYY-MM-DD
	originalCodes?: OriginalCodeInput[]
}

/**
 * Body for updating a work order — mirrors `workOrderUpdateSchema` (partial,
 * nullable). The edit form sends the full field set every save; `null` clears an
 * optional field, `undefined` leaves it untouched.
 */
export interface UpdateWorkOrderInput {
	title?: string
	briefing?: string | null
	dueAt?: string | null
	parentWorkOrderId?: string | null
	pmUserId?: string | null
	isContentFactory?: boolean
	tuple?: Selection
	complexityLevel?: ComplexityLevel | null
	assetTypeCode?: AssetTypeCode | null
	veevaCode?: string | null
	durationSeconds?: number | null
	campaignCode?: string | null
	links?: WorkOrderLink[]
	assigneeIds?: string[]
	budget?: MoneyInput | null
	plannedCost?: MoneyInput | null
	finalCost?: MoneyInput | null
	expectedPublishingDate?: string | null
	originalCodes?: OriginalCodeInput[]
	/** An `aspect_ratio` value code. */
	aspectRatio?: string | null
	/** A `channel` value code: where the piece runs, stored as the tuple's channel. */
	platform?: string | null
	/** What the PM writes down for the designer; absent fields are left alone. */
	pack?: WorkOrderPackUpdateInput
}

/**
 * The combined display code — asset-type then complexity (e.g. D5 + L4 → "D5L4"),
 * matching the validated design. Derived, never stored.
 */
export function combinedComplexityCode(wo: {
	complexityLevel: string | null
	assetTypeCode: string | null
}): string | null {
	if (!wo.complexityLevel && !wo.assetTypeCode) return null
	return `${wo.assetTypeCode ?? ''}${wo.complexityLevel ?? ''}` || null
}

/** The configured statuses of the order's own workflow; empty when it has none on the page. */
export function statusesOf(
	wo: Pick<WorkOrder, 'workflowId'>,
	workflows: readonly BoardWorkflow[],
): readonly BoardStatus[] {
	if (!wo.workflowId) return []
	return workflows.find((w) => w.workflowId === wo.workflowId)?.statuses ?? []
}

/**
 * The `BoardStatus` a work order currently occupies, found by matching its
 * `workflowId` + `status` code against the page's workflow grouping model.
 * `undefined` when the order has no workflow, or its workflow/status is not
 * among the page's configured set (unconfigured, or a legacy row that has not
 * been assigned a workflow yet).
 */
export function findBoardStatus(
	wo: Pick<WorkOrder, 'workflowId' | 'status'>,
	workflows: readonly BoardWorkflow[],
): BoardStatus | undefined {
	return statusesOf(wo, workflows).find((s) => s.code === wo.status)
}

/**
 * Whether a work order is in a terminal status. Reads the assigned workflow's
 * own `isTerminal` configuration when the order's workflow/status resolves
 * against the page's workflow model. An order that model cannot place (no
 * workflow, or one absent from the read) has no configuration to consult, so
 * the conventional `completed` and `cancelled` codes are the only signal left.
 */
export function isTerminalStatus(
	wo: Pick<WorkOrder, 'workflowId' | 'status'>,
	workflows: readonly BoardWorkflow[],
): boolean {
	const boardStatus = findBoardStatus(wo, workflows)
	return boardStatus ? boardStatus.isTerminal : wo.status === 'completed' || wo.status === 'cancelled'
}

/**
 * Whether the order is sitting on a validation lane right now. Leaving one is a
 * reviewer action (ADR-0031); arriving on one is ordinary work, so this asks
 * only about where the order IS, never about where it is going.
 *
 * Read off the status's configured `phase` rather than its code, so an
 * administrator who renames a lane, adds a third or removes one does not
 * silently reopen the gate. An order whose status the board model cannot place
 * has no configuration to consult and is treated as not under review: the
 * server is authoritative and refuses it anyway.
 */
export function isUnderReview(
	wo: Pick<WorkOrder, 'workflowId' | 'status'>,
	workflows: readonly BoardWorkflow[],
): boolean {
	return findBoardStatus(wo, workflows)?.phase === WorkflowPhase.VALIDATION
}

/**
 * Whether `userId` may take the order back out of review to `toStatus` without a
 * reviewer: they are assigned to it, and the move is a withdrawal from the first
 * review (`isWithdrawal`). `statuses` is the order's own workflow, from the board
 * or the workspace snapshot alike. The server applies the same rule and is the
 * authority.
 */
export function mayWithdraw(
	wo: Pick<WorkOrder, 'status' | 'assigneeIds'>,
	toStatus: string | undefined,
	statuses: readonly Pick<BoardStatus, 'code' | 'phase' | 'position'>[],
	userId: string | undefined,
): boolean {
	if (!userId || !wo.assigneeIds.includes(userId)) return false
	const current = statuses.find((s) => s.code === wo.status)
	const target = statuses.find((s) => s.code === toStatus)
	return !!current && !!target && isWithdrawal(current, target, statuses)
}

/**
 * The board's default view: everything EXCEPT terminal cards whose status last
 * changed more than `DONE_VISIBLE_DAYS` (board config) ago — so freshly-completed
 * work lingers briefly, then drops off. `now` injectable for tests.
 */
export function isVisibleByDefault(
	wo: Pick<WorkOrder, 'workflowId' | 'status' | 'statusChangedAt'>,
	workflows: readonly BoardWorkflow[],
	now: number = Date.now(),
): boolean {
	if (!isTerminalStatus(wo, workflows)) return true
	const ageDays = (now - new Date(wo.statusChangedAt).getTime()) / 86_400_000
	return ageDays <= DONE_VISIBLE_DAYS
}

/**
 * The shared `{workOrder, options}` contract every workspace catalog component
 * implements, so the registry can render any of the six kinds interchangeably.
 * Lives here rather than beside the other workflow types because it references
 * `WorkOrder`, and `workflow.types.ts` is imported BY this module.
 */
export interface WorkspaceComponentProps {
	workOrder: WorkOrder
	options: Record<string, unknown>
}
